import React from 'react';
import { useSparkBuilderStore } from '.';
import { useQueryClient } from 'react-query';
import type { SparkStep, SparkStepAttribute } from 'types/SparkStep';
import type { NewStepOrdersType } from 'components/CreateSpark/SparkBuilderCanvas/types';
import type { StepTypeIDMap } from 'types/store/sparkBuilder';

type MinimalSparkStep = {
  id: string;
  stepOrder: number;
  stepTypeID: string;
  memoryID?: string | null;
  pluginID?: string | null;
};

const invalidateQueriesList = [
  'SparkOutputReferencesReferences',
  'sparkStep',
  'sparkDiagnostics',
  'sparkSteps',
];

export const useSparkStepTypeHelper = () => {
  const setSparkStepTypeIdMap = useSparkBuilderStore(
    (store) => store.sparkStepsActions.setSparkStepTypeIdMap
  );
  const onSparkStepTypeSuccess = (stepTypeIdMap: StepTypeIDMap | undefined) => {
    if (!!stepTypeIdMap) setSparkStepTypeIdMap(stepTypeIdMap);
  };
  return onSparkStepTypeSuccess;
};

export const useCommonActionHelper = () => {
  const queryClient = useQueryClient();

  const restorePreviousSparkBuilderState = useSparkBuilderStore(
    (store) => store.sparkStepsActions.restorePreviousSparkBuilderState
  );

  const updateReactFlowElements = useSparkBuilderStore((store) =>
    store.canvasActions.useReactFlowElements()
  );
  const updatePreviousState = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updatePreviousState
  );

  const commonActions = {
    onSuccess: () => {
      updatePreviousState();
      invalidateQueriesList.forEach(
        async (query) => await queryClient.invalidateQueries([query])
      );
      queryClient.removeQueries(['sparkStepReferences']);
    },
    invalidateQuery: () => {
      invalidateQueriesList.forEach(
        async (query) => await queryClient.invalidateQueries([query])
      );
    },
    onError: () => {
      restorePreviousSparkBuilderState();
      updateReactFlowElements();
    },
    onErrorRestoreState: () => restorePreviousSparkBuilderState(),
  };
  return commonActions;
};

export const useSparkStepsBySparkIDHelper = () => {
  const setSparkSteps = useSparkBuilderStore(
    (store) => store.sparkStepsActions.setSparkSteps
  );
  const setSparkStepState = useSparkBuilderStore(
    (store) => store.sparkStepsActions.setSparkStepState
  );
  const updateSortOrder = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSortOrder
  );
  const orderActions = {
    onSuccess: (sparkSteps: SparkStep[]) => {
      setSparkSteps(sparkSteps);
      updateSortOrder();
    },
    setState: (isLoading: boolean, isFetching: boolean) => {
      setSparkStepState(isLoading, isFetching);
    },
  };
  return orderActions;
};
export const useUpdateSparkStepHelper = () => {
  const commonActions = useCommonActionHelper();

  const updateStateSparkStep = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSparkStep
  );
  const updateSparkStepAttributes = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSparkStepAttributes
  );

  const orderActions = {
    onMutate: (
      sparkStepAttributes: Partial<SparkStepAttribute>,
      id: string
    ) => {
      updateSparkStepAttributes(sparkStepAttributes, id);
    },
    onSuccess: (sparkStep: SparkStep) => {
      updateStateSparkStep(sparkStep, sparkStep.id);
      commonActions.onSuccess();
    },
    onError: () => {
      commonActions.onErrorRestoreState();
    },
  };
  return orderActions;
};
export const useDeleteSparkStepHelper = () => {
  const commonActions = useCommonActionHelper();

  const deleteStateSparkStep = useSparkBuilderStore(
    (store) => store.sparkStepsActions.deleteSparkStep
  );
  const updateSortOrder = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSortOrder
  );
  const updateReactFlowElements = useSparkBuilderStore((store) =>
    store.canvasActions.useReactFlowElements()
  );
  const orderActions = {
    onMutate: (stepID: string) => {
      deleteStateSparkStep(stepID);
      updateSortOrder();
      updateReactFlowElements();
    },
    onSuccess: () => {
      commonActions.onSuccess();
    },
    onError: () => {
      commonActions.onError();
    },
  };
  return orderActions;
};
export const useAddSparkStepHelper = () => {
  const commonActions = useCommonActionHelper();
  const addSparkStepToState = useSparkBuilderStore(
    (store) => store.sparkStepsActions.addSparkStep
  );

  const updateSparkStep = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSparkStep
  );

  const updateSortOrder = useSparkBuilderStore(
    (store) => store.sparkStepsActions.updateSortOrder
  );
  const updateReactFlowElements = useSparkBuilderStore((store) =>
    store.canvasActions.useReactFlowElements()
  );

  const sparkStateID = useSparkBuilderStore((store) => store.sparkID);

  const orderActions = {
    onMutate: (minimalStep: MinimalSparkStep) => {
      const { stepOrder, stepTypeID, memoryID, pluginID, id } = minimalStep;
      const partialNewStep: SparkStep = {
        type: 'sparkSteps',
        id,
        attributes: {
          sparkID: sparkStateID,
          stepOrder: stepOrder,
          sparkStepTypeID: stepTypeID,
          pluginConnectionID: null,
          name: `Step ${stepOrder}`,
          description: `Step ${stepOrder} description`,
          promptFunctionID: null,
          pluginFunctionID: null,
          pluginActionID: null,
          memorySearchID: null,
          pluginID: pluginID || null,
          note: '',
          isDeleted: false,
          memoryID: memoryID || '',
          sparkTaskStatusTypeID: 1,
          deletedAt: '',
          createdAt: '',
          createdBy: '',
          modifiedAt: null,
          modifiedBy: '',
          testResults: null,
        },
        relationships: {} as any,
        meta: { diagnostics: { hasIssues: true } } as any,
        links: {} as any,
      };
      addSparkStepToState(partialNewStep);
      updateSortOrder();
      updateReactFlowElements();
    },
    onSuccess: (sparkStep: SparkStep | undefined) => {
      if (!!sparkStep) {
        updateSparkStep(sparkStep, sparkStep.id);
        commonActions.onSuccess();
      }
    },
    onError: () => {
      commonActions.onError();
    },
  };
  return orderActions;
};

export const useSparkStepOrderHelper = () => {
  const updateStepAndCheckReference = useSparkBuilderStore(
    React.useCallback(
      (state) => state.sparkStepsActions.updateStepAndCheckReference,
      []
    )
  );

  const commonActions = useCommonActionHelper();
  const reArrangeSparkSteps = useSparkBuilderStore(
    (store) => store.sparkStepsActions.reArrangeSparkSteps
  );

  const onStepOrderMutate = (stepOrder: NewStepOrdersType[]) => {
    reArrangeSparkSteps(stepOrder);
  };
  const orderActions = {
    onMutate: (stepOrder: NewStepOrdersType[]) => {
      onStepOrderMutate(stepOrder);
    },
    onSuccess: (sparkSteps: SparkStep[], isQueryFinished: boolean) => {
      const isReferenceBreaking = sparkSteps
        .map((step) => {
          return updateStepAndCheckReference(step);
        })
        .some(Boolean);
      if (isQueryFinished) commonActions.invalidateQuery();
      if (!isReferenceBreaking) commonActions.onSuccess();
    },
    onError: () => {
      commonActions.onError();
    },
  };
  return orderActions;
};
